import React from 'react';
import { useTheme } from '../context/ThemeContext';
import { Users, Mail, Phone, Briefcase, Award, Shield } from 'lucide-react';
import RotatingText from '../components/common/RotatingText';

const ExecutiveCommitteePage = () => {
  const { isDarkMode } = useTheme();

  // Executive Committee Members Data
  const executives = [
    {
      id: 1,
      name: 'John Smith',
      position: 'President',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop',
      email: 'president@fsgu.co.za',
      phone: '+27 51 123 4567',
      bio: 'Leading the Free State Golf Union with over 20 years of experience in golf administration.',
      icon: Award
    },
    {
      id: 2,
      name: 'Sarah Johnson',
      position: 'Vice President',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop',
      email: 'vicepresident@fsgu.co.za',
      phone: '+27 51 123 4568',
      bio: 'Dedicated to promoting women\'s golf and junior development across the province.',
      icon: Users
    },
    {
      id: 3,
      name: 'Michael Williams',
      position: 'Chairman',
      image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop',
      email: 'chairman@fsgu.co.za',
      phone: '+27 51 123 4569',
      bio: 'Overseeing strategic planning and governance with a focus on growth and excellence.',
      icon: Briefcase
    },
    {
      id: 4,
      name: 'David Brown',
      position: 'Secretary',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop',
      email: 'secretary@fsgu.co.za',
      phone: '+27 51 123 4570',
      bio: 'Managing administrative operations and ensuring smooth communication across all clubs.',
      icon: Shield
    },
    {
      id: 5,
      name: 'Lisa Anderson',
      position: 'Treasurer',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop',
      email: 'treasurer@fsgu.co.za',
      phone: '+27 51 123 4571',
      bio: 'Overseeing financial management and ensuring fiscal responsibility.',
      icon: Briefcase
    },
    {
      id: 6,
      name: 'Robert Taylor',
      position: 'Tournament Director',
      image: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=400&h=400&fit=crop',
      email: 'operations@fsgu.co.za',
      phone: '+27 51 123 4572',
      bio: 'Coordinating all provincial tournaments and ensuring high standards of competition.',
      icon: Award
    }
  ];

  const responsibilities = [
    {
      icon: Users,
      title: 'Governance',
      description: 'Providing strategic direction and oversight for all FSGU operations and activities.'
    },
    {
      icon: Award,
      title: 'Tournament Management',
      description: 'Organizing and overseeing provincial golf tournaments and championships.'
    },
    {
      icon: Shield,
      title: 'Club Relations',
      description: 'Maintaining strong relationships with all 45 affiliated golf clubs in the Free State.'
    },
    {
      icon: Briefcase,
      title: 'Development',
      description: 'Promoting golf development programs for juniors, women, and seniors across the province.'
    }
  ];

  return (
    <div className={`min-h-screen ${
      isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
    }`}>
      {/* Hero Section - Matches other pages */}
      <section className="relative min-h-[85vh] md:min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/exec.png" 
            alt="Executive Committee" 
            className="w-full h-full object-cover"
          />
          <div className={`absolute inset-0 ${
            isDarkMode 
              ? 'bg-gradient-to-b from-fsgu-gray-900/60 via-fsgu-gray-900/70 to-fsgu-gray-900/90' 
              : 'bg-gradient-to-b from-black/40 via-black/50 to-black/70'
          }`}></div>
        </div>

        {/* Content */}
        <div className="container-custom px-4 relative z-10 text-center">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold font-cursive mb-8 md:mb-12 text-white leading-tight tracking-tight">
              Executive Committee
            </h1>
            
            {/* Rotating Text Section */}
            <div className="flex flex-wrap items-center justify-center gap-3 text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-light mb-8">
              <span className="drop-shadow-lg text-white">Leading with</span>
              <RotatingText
                texts={[
                  'Excellence',
                  'Integrity',
                  'Dedication',
                  'Vision',
                  'Passion',
                ]}
                mainClassName={`font-cursive font-normal px-6 py-3 pb-4 rounded-2xl ${
                  isDarkMode
                    ? 'bg-fsgu-green-700/95 text-white'
                    : 'bg-fsgu-green-700 text-white'
                } drop-shadow-2xl`}
                splitLevelClassName="overflow-visible"
                staggerFrom="first"
                initial={{ y: '100%', opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: '-120%', opacity: 0 }}
                staggerDuration={0.03}
                transition={{ type: 'spring', damping: 30, stiffness: 400 }}
                rotationInterval={3000}
              />
            </div>

            <p className="text-lg sm:text-xl md:text-2xl text-white/90 font-light max-w-3xl mx-auto leading-relaxed">
              Meet the dedicated leaders guiding the Free State Golf Union
            </p>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-10 hidden md:block">
          <div className="animate-bounce">
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </div>
        </div>
      </section>

      {/* Committee Responsibilities */}
      <section className="section">
        <div className="container-custom">
          <h2 className={`text-3xl sm:text-4xl font-bold text-center mb-12 ${
            isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
          }`}>
            Our Responsibilities
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {responsibilities.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div
                  key={index}
                  className={`p-6 rounded-2xl border text-center transition-all duration-300 hover:scale-105 ${
                    isDarkMode
                      ? 'bg-fsgu-gray-800 border-fsgu-gray-700 hover:border-fsgu-green-500/50'
                      : 'bg-fsgu-white border-fsgu-silver-200 hover:border-fsgu-green-500/50 shadow-sm'
                  }`}
                >
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4 ${
                    isDarkMode ? 'bg-fsgu-green-500/20' : 'bg-fsgu-green-500/10'
                  }`}>
                    <IconComponent className={`w-6 h-6 ${
                      isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                    }`} />
                  </div>
                  <h3 className={`text-lg font-semibold mb-2 ${
                    isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                  }`}>
                    {item.title}
                  </h3>
                  <p className={`text-sm ${
                    isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                  }`}>
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Committee Members */}
      <section className={`section ${
        isDarkMode ? 'bg-fsgu-gray-800/50' : 'bg-fsgu-white'
      }`}>
        <div className="container-custom">
          {/* <h2 className={`text-3xl sm:text-4xl font-bold text-center mb-12 ${
            isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
          }`}>
            Committee Members
          </h2> */}

           {/* comittee section  */}

           
        </div>
      </section>

      {/* Contact CTA */}
      <section className="section">
        <div className="container-custom">
          <div className={`max-w-3xl mx-auto text-center p-8 sm:p-12 rounded-2xl border ${
            isDarkMode
              ? 'bg-gradient-to-br from-fsgu-green-900/20 to-fsgu-blue-900/20 border-fsgu-gray-700'
              : 'bg-gradient-to-br from-fsgu-green-50 to-fsgu-blue-50 border-fsgu-silver-200'
          }`}>
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6 ${
              isDarkMode ? 'bg-fsgu-green-500/20' : 'bg-fsgu-green-500/10'
            }`}>
              <Mail className={`w-8 h-8 ${
                isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
              }`} />
            </div>

            <h2 className={`text-3xl sm:text-4xl font-bold mb-4 font-cursive ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              Get in Touch
            </h2>

            <p className={`text-lg mb-8 ${
              isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
            }`}>
              Have questions or need assistance? Our executive committee is here to help.
            </p>

            <a
              href="/contact"
              className="btn btn-primary inline-flex items-center"
            >
              <Mail className="w-5 h-5 mr-2" />
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ExecutiveCommitteePage;